import React, { ReactElement, useMemo } from 'react';
import useQueryParams from '~/utils/hooks/useQueryParams';
import BaseTable from '~/components/Table/Base/BaseTable';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  sortingFns,
  useReactTable,
} from '@tanstack/react-table';
import formatCurrency from '~/utils/formatCurrency';
import { formatEndDateForTable } from '~/pages/Expenses/utils/formatEndDateForTable';
import { convertDepartmentUuidsToDisplayName } from '~/pages/Expenses/utils/convertDepartmentUuidsToDisplayName';
import expenseModelEmptyState from '~/assets/expenseModelEmptyState.svg';
import Typography from '~/components/Typography';
import EllipsisDropdown from '~/components/EllipsisDropdown';
import { IExpenseTableRow, IExpense } from '../types';
import formatPercent from '~/utils/formatPercent';
import { sortAmountFn, sortDateFn } from '~/pages/Expenses/components/Table/customSortingFunctions';
import { useSelector } from 'react-redux';
import { State } from '~/store';
import Button from '~/components/Button';
import HoverPopover from '~/components/HoverPopover';
import * as stringDate from '~/utils/stringDate';
import type { IFormula } from '~/services/parallel/formulas.types';
import { departmentsApi } from '~/services/parallel/api/departmentsApi';
import { filterExpenseFormulas } from './filterExpenseFormulas';
import type { IIntegrationWithMappings } from '~/services/parallel/integrations.types';

const TYPE_DISPLAY_MAP = {
  setCost: 'Set Cost',
  headcountFixed: 'Amount per Employee',
  headcountPercentCompensation: 'Percent of Salary',
  custom: 'Custom',
};

const FREQUENCY_DISPLAY_MAP = {
  monthly: 'Monthly',
  oneTime: 'One Time',
  onHire: 'On Hire',
  quarterly: 'Quarterly',
  annually: 'Annually',
};

export const PERCENTAGE_CONVERSION_DENOMINATOR = 10000;

interface ExpenseDetailTableProps {
  // Data
  formulas: IFormula[];
  formulasLoading: boolean; // Loading state from RTK Query

  // Modal callbacks
  onEdit: (formulaUuid: string) => void;
  onDuplicate: (formulaUuid: string) => void;
  onDelete: (formulaUuid: string) => void;
  onDiscontinue: (formulaUuid: string) => void;

  // Optional props
  className?: string;
  showPastExpenses?: boolean;
}

export const ExpenseDetailTable = ({
  formulasLoading,
  formulas,
  onEdit,
  onDuplicate,
  onDelete,
  onDiscontinue,
}: ExpenseDetailTableProps): ReactElement => {
  const { activeScenarioUuid } = useSelector((state: State) => state.scenario);
  const { uuid: orgUuid } = useSelector((state: State) => state.organization);
  const { queryParams } = useQueryParams();
  const selectedCategory = queryParams.get('category');
  const showPastExpenses = queryParams.get('showPastExpenses') === 'true';
  const selectedFrequency = queryParams.get('frequency');
  const searchFilter = queryParams.get('search') ?? '';

  const { data: departments } = departmentsApi.useGetDepartmentsQuery({ orgUuid });

  const departmentDict = departments?.dictionary ?? {};

  const filteredFormulas: IFormula[] = useMemo(() => {
    return filterExpenseFormulas({
      expenseFormulas: formulas,
      search: searchFilter,
      category: selectedCategory,
      frequency: selectedFrequency,
      showPastExpenses,
    });
  }, [formulas, selectedCategory, selectedFrequency, showPastExpenses, searchFilter]);

  /**
   * Generate table data
   */
  const { tableData, tableColumns } = useMemo(() => {
    const columns = [
      { id: 'name', label: 'NAME', enableSorting: true },
      { id: 'tag', label: 'CATEGORY', enableSorting: true },
      { id: 'driver', label: 'TYPE', enableSorting: true },
      { id: 'department', label: 'DEPARTMENT', enableSorting: true },
      {
        id: 'amount',
        label: 'AMOUNT',
        enableSorting: true,
        sortingFn: sortAmountFn,
      },
      {
        id: 'frequency',
        label: 'FREQUENCY',
        enableSorting: true,
      },
      { id: 'startDate', label: 'START DATE', enableSorting: true, sortingFn: sortDateFn },
      { id: 'endDate', label: 'END DATE' },
      { id: 'ellipsisDropdown', label: '' },
    ];

    const columnHelper = createColumnHelper<IExpenseTableRow>();

    const tableColumns = columns.map((col) =>
      columnHelper.accessor(col.id, {
        enableResizing: false,
        enablePinning: false,
        header: () => <div className="text-left">{col.label}</div>,
        cell: (info) => (
          <Typography
            color={info.row.original.isGrayedOut ? 'disabled' : undefined}
            id={`expense-${col.id}-${info.row.index}`}
          >
            {info.row.original[col.id]}
          </Typography>
        ),
        enableSorting: col.enableSorting ?? false,
        sortingFn: col.sortingFn ?? sortingFns.alphanumeric,
      }),
    );

    const tableData = filteredFormulas.map(({ recipe, context, formulaUuid }, index) => {
      const expenseContext = context?.expenseContext;
      if (!expenseContext) {
        return null;
      }
      let amount;
      if (expenseContext.driver === 'custom') {
        amount = '-';
      } else {
        amount =
          expenseContext.driver === 'headcountPercentCompensation'
            ? formatPercent({
                value: (expenseContext.amount as number) / PERCENTAGE_CONVERSION_DENOMINATOR,
              })
            : formatCurrency(expenseContext.amount as number);
      }
      return {
        name: recipe.name,
        tag: expenseContext.tag,
        driver: TYPE_DISPLAY_MAP[expenseContext.driver],
        department: (
          <HoverPopover
            buttonContent={
              <div className="!max-w-[200px] truncate">
                {convertDepartmentUuidsToDisplayName({
                  departmentUuids: expenseContext.departments,
                  departmentDict,
                })}
              </div>
            }
            panelContent={
              <div className="bg-black px-2 py-1 rounded-lg items-center justify-center">
                <Typography color="white">
                  {convertDepartmentUuidsToDisplayName({
                    departmentUuids: expenseContext.departments,
                    departmentDict,
                  })}
                </Typography>
              </div>
            }
            hoverDelay={500}
          />
        ),
        amount,
        frequency: expenseContext.driver === 'custom' ? '-' : FREQUENCY_DISPLAY_MAP[expenseContext.frequency],
        startDate: stringDate.format(recipe.startDate ?? '', 'MMMM yyyy'),
        endDate: formatEndDateForTable({
          endDate: recipe.endDate ?? null,
          frequency: expenseContext.frequency,
        }),
        ellipsisDropdown: (
          <EllipsisDropdown
            id={`expense-ellipsis-dropdown-${formulaUuid}`}
            placeTop={Boolean(
              activeScenarioUuid && filterExpenseFormulas.length > 1 && index === filterExpenseFormulas.length - 1,
            )}
            options={[
              {
                label: 'Edit',
                onClick: (): void => {
                  onEdit(formulaUuid);
                },
                className: 'max-sm:hidden',
              },
              {
                label: 'Duplicate',
                onClick: (): void => {
                  onDuplicate(formulaUuid);
                },
                className: 'max-sm:hidden',
              },
              ...(expenseContext.frequency !== 'oneTime'
                ? [
                    {
                      label: 'Discontinue',
                      onClick: () => onDiscontinue(formulaUuid),
                      className: 'max-sm:hidden',
                    },
                  ]
                : []),
              {
                label: 'Delete',
                onClick: () => onDelete(formulaUuid),
                className: 'text-red-500 max-sm:hidden',
              },
            ]}
          />
        ),
      };
    });

    return { tableData, tableColumns };
  }, [filteredFormulas]);

  const table = useReactTable({
    columns: tableColumns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const noResultsEmptyState = (
    <div className="flex flex-col items-center gap-2 py-16">
      <img src={expenseModelEmptyState} alt="Empty Tasks Illustration" className="w-64 h-auto" />
      <div className="flex flex-col items-center gap-4 max-w-[512px] text-center">
        <Typography color="secondary" size="sm">
          No matching expenses
        </Typography>
        <Typography color="secondary" size="xs">
          Try adjusting your filters or search criteria
        </Typography>
      </div>
    </div>
  );

  return (
    <div>
      <div className="max-w-full w-full overflow-scroll hide-scrollbar px-10">
        <BaseTable
          id="expenses-table"
          // @ts-expect-error - tanstack table doesn't like typed rows
          table={table}
          emptyState={noResultsEmptyState}
          styles={{
            table: 'w-full',
            th: 'px-4 py-2 text-xs font-normal text-neutral-200 text-nowrap',
            td: 'h-14 px-4 border-t border-b border-gray-200 text-nowrap',
          }}
        />
      </div>
    </div>
  );
};
